import React, { useEffect, useState } from 'react';
import { fetchJson } from '../apiBase';
import { avatarImageUrl } from '../shared/studentAvatar';
import ParentStudentPageLayout from './ParentStudentPageLayout';
import './AvatarPage.css';

async function avatarRequest(path, options) {
  const response = await fetchJson(path, options);
  const body = await response.json();
  if (!response.ok) throw new Error(body?.detail || 'Avatar bilgileri yüklenemedi.');
  return body;
}

function AvatarPicture({ avatarId, emoji, size = 128 }) {
  return avatarId ? <img src={avatarImageUrl(avatarId, size)} alt="" loading="lazy" decoding="async" />
    : <span role="img" aria-label="Başlangıç avatarı">{emoji}</span>;
}

export default function AvatarPage() {
  const requestedId = new URLSearchParams(window.location.search).get('student_id') || '';
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState(requestedId);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmAvatar, setConfirmAvatar] = useState(null);

  useEffect(() => {
    let active = true;
    avatarRequest('/parent/allowed-students').then((body) => {
      if (!active) return;
      const items = body?.items || [];
      setStudents(items);
      if (!requestedId && items.length) setStudentId(String(items[0].student_id));
      if (!requestedId && !items.length) { setError('Gösterilecek öğrenci bulunamadı.'); setLoading(false); }
    }).catch((err) => {
      if (active) { setError(err.message); setLoading(false); }
    });
    return () => { active = false; };
  }, [requestedId]);

  useEffect(() => {
    if (!studentId) return undefined;
    let active = true;
    setLoading(true);
    setData(null);
    setError('');
    avatarRequest(`/parent/avatars?student_id=${encodeURIComponent(studentId)}`)
      .then((body) => { if (active) setData(body); })
      .catch((err) => { if (active) setError(err.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [studentId]);

  async function changeAvatar(avatarId, purchase = false) {
    setBusy(true);
    setError('');
    try {
      const body = await avatarRequest(`/parent/avatars/${purchase ? 'purchase' : 'equip'}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ student_id: Number(studentId), avatar_id: avatarId }),
      });
      setData(body);
      setConfirmAvatar(null);
    } catch (err) {
      setError(err.message);
      setConfirmAvatar(null);
    } finally {
      setBusy(false);
    }
  }

  const active = data?.catalog?.find((avatar) => avatar.id === data.active_avatar_id);
  const owned = data?.catalog?.filter((avatar) => avatar.owned) || [];
  const selectedStudent = students.find((student) => String(student.student_id) === String(studentId));

  return <ParentStudentPageLayout studentId={studentId} studentFallback={selectedStudent} students={students}
    pointBalance={data?.available_points} activeAvatarId={data?.active_avatar_id} starterEmoji={data?.starter_emoji} activePage="avatar">
    <main className="avatar-page">
    <div className="avatar-shell">
      <header className="avatar-heading">
        <p>Kazandığın puanlarla sevdiğin avatarı seç. Aldığın avatarlar hep senin kalır.</p>

      </header>
      {error && <div className="avatar-error" role="alert">{error}</div>}
      {loading && <p className="avatar-loading">Avatarlar yükleniyor...</p>}
      {!loading && data && <>
        <section className="avatar-hero" aria-label="Seçili avatar">
          <div className="avatar-hero-picture"><AvatarPicture avatarId={active?.id} emoji={data.starter_emoji} size={256} /></div>
          <div><span className="avatar-eyebrow">Şu anki avatarın</span><h2>{active?.name || 'Başlangıç avatarın'}</h2>
            <p>{selectedStudent ? `${selectedStudent.first_name} ${selectedStudent.last_name}` : 'Senin avatarın'}</p>
            <div className="avatar-balances"><span><strong>{data.available_points}</strong> kullanılabilir puan</span><span><strong>{data.total_earned_points}</strong> toplam kazanılan</span></div>
          </div>
        </section>

        <section className="avatar-section" aria-labelledby="inventory-heading">
          <div className="avatar-section-title"><div><span className="avatar-eyebrow">Koleksiyonun</span><h2 id="inventory-heading">Avatarlarım</h2></div><span>{owned.length + 1} avatar</span></div>
          <div className="avatar-inventory">
            <button type="button" className={`avatar-owned${!data.active_avatar_id ? ' is-active' : ''}`} disabled={busy || !data.active_avatar_id}
              onClick={() => changeAvatar('starter')}><AvatarPicture emoji={data.starter_emoji} /><strong>Başlangıç avatarım</strong><small>{!data.active_avatar_id ? 'Kullanılıyor' : 'Kullan'}</small></button>
            {owned.map((avatar) => <button type="button" key={avatar.id} className={`avatar-owned${data.active_avatar_id === avatar.id ? ' is-active' : ''}`}
              disabled={busy || data.active_avatar_id === avatar.id} onClick={() => changeAvatar(avatar.id)}>
              <AvatarPicture avatarId={avatar.id} /><strong>{avatar.name}</strong><small>{data.active_avatar_id === avatar.id ? 'Kullanılıyor' : 'Kullan'}</small>
            </button>)}
          </div>
        </section>

        <section className="avatar-section" aria-labelledby="shop-heading">
          <div className="avatar-section-title"><div><span className="avatar-eyebrow">Yeni bir görünüm</span><h2 id="shop-heading">Avatar Mağazası</h2></div><span>Her avatar 60 puan</span></div>
          <div className="avatar-shop">
            {data.catalog.map((avatar) => {
              const isActive = data.active_avatar_id === avatar.id;
              const soldOut = !avatar.owned && avatar.class_owner_count >= avatar.class_owner_limit;
              const canAfford = data.available_points >= avatar.price;
              return <article className="avatar-shop-card" key={avatar.id}>
                <div className="avatar-shop-picture"><AvatarPicture avatarId={avatar.id} /></div>
                <h3>{avatar.name}</h3>
                <p>{avatar.owned ? 'Bu avatara sahipsin' : soldOut ? 'Bu avatar sınıfında tükendi' : `${avatar.price} DANDU puanı`}</p>
                {avatar.owned ? <button type="button" disabled={busy || isActive} onClick={() => changeAvatar(avatar.id)}>{isActive ? 'Kullanılıyor' : 'Kullan'}</button>
                  : <button type="button" disabled={busy || soldOut || !canAfford} onClick={() => setConfirmAvatar(avatar)}>
                    {soldOut ? 'Sınıfta tükendi' : canAfford ? `${avatar.price} puana al` : 'Puan biriktir'}
                  </button>}
              </article>;
            })}
          </div>
        </section>
      </>}
    </div>
    {confirmAvatar && <div className="avatar-dialog-backdrop" role="presentation" onClick={() => !busy && setConfirmAvatar(null)}>
      <div className="avatar-dialog" role="dialog" aria-modal="true" aria-labelledby="avatar-confirm-title" onClick={(event) => event.stopPropagation()}>
        <div className="avatar-dialog-picture"><AvatarPicture avatarId={confirmAvatar.id} size={256} /></div>
        <h2 id="avatar-confirm-title">{confirmAvatar.name}</h2>
        <p>Bu avatarı 60 puana almak istiyor musun? Satın aldıktan sonra hemen kullanabilirsin.</p>
        <div><button type="button" className="avatar-cancel" disabled={busy} onClick={() => setConfirmAvatar(null)}>Vazgeç</button>
          <button type="button" disabled={busy} onClick={() => changeAvatar(confirmAvatar.id, true)}>{busy ? 'Alınıyor...' : '60 puana al'}</button></div>
      </div>
    </div>}
    </main>
  </ParentStudentPageLayout>;
}
